import { Injectable, inject } from '@angular/core';
import { registerLocaleData  } from '@angular/common';
import { BehaviorSubject } from 'rxjs';
import {
  Locale,
  LocaleDetails,
  LocaleLanguage
} from './locale.domain';
import { LocaleLoaderService } from './locale-loader.service';
import type {
  CalendarStrings } from './locale.domain';

@Injectable({
  providedIn: 'root'
})
export class LocaleService {
  private readonly localeLoaderService = inject<LocaleLoaderService>(LocaleLoaderService);

  readonly onLocaleChange: BehaviorSubject<Locale>;

  private locale: Locale = Locale.en_CA;
  private language: LocaleLanguage = LocaleLanguage.EN;
  private localeDetails: LocaleDetails = new LocaleDetails(this.locale, '', '', '');

  constructor() {
    this.onLocaleChange = new BehaviorSubject<Locale>(this.locale);
  }

  /**
   * Converts java locale e.g. en_CA to angular locale en-ca
   *
   * @param javaLocale
   * @returns
   */
  static convertLocale(javaLocale: string): string {
    return javaLocale.replace('_', '-').toLocaleLowerCase();
  }

  /**
   * Loads translations and sets application's locale
   *
   * @param localeString en_CA, en-ca, fr_CA, fr-ca
   * @returns promise resolving when translations are loaded
   */
  async setLocale(localeString: string): Promise<void> {
    const locale = LocaleService.convertLocale(localeString) as Locale;
    const language = (locale as string).split('-')[0] as LocaleLanguage;

    const localeWithTranslation = await this.localeLoaderService.load(locale);
    this.locale = locale;
    this.language = language;
    registerLocaleData(localeWithTranslation.localeData, locale);
    this.initLocaleDetails(locale, localeWithTranslation.localeData);
    this.onLocaleChange.next(this.locale);
  }

  getLocaleDetails(): LocaleDetails {
    return this.localeDetails;
  }

  getLocale(): Locale {
    return this.locale;
  }

  getLanguage(): LocaleLanguage {
    return this.language;
  }

  private initLocaleDetails(locale: Locale, localeData: Array<string>): void {
    const normalizedLocaleData = localeData as unknown as Array<unknown>;
    const numberSymbols = normalizedLocaleData[13] as [string, string];
    const groupSeparator: string = numberSymbols[1];
    const decimalSeparator: string = numberSymbols[0];
    const currencySymbol: string = normalizedLocaleData[16] as string;

    this.localeDetails = new LocaleDetails(
      locale,
      groupSeparator,
      decimalSeparator,
      currencySymbol,
      this.getCalendarStrings(localeData)
    );
  }

  private getCalendarStrings(localeData: Array<unknown>): CalendarStrings {
    const normalizedLocaleData = localeData;
    const monthData = normalizedLocaleData[5] as [unknown, Array<string>, Array<string>];
    const weekdayData = normalizedLocaleData[3] as [unknown, unknown, Array<string>];
    return {
      months: [...monthData[2]],
      weekdays: [...weekdayData[2]],
      shortMonths: [...monthData[1]]
    };
  }
}
